import { useEffect, useRef, useState } from 'react';

import { useEffectEvent } from '../../../../hooks/useEffectEvent';
import { useVideo } from '../../../../store/videos/useVideos';
import { useApi } from '../../../../store/SelectedHomeyStore';
import { categorizeVideoError, CategorizedVideoError } from './videoErrorUtils';

const ICE_GATHERING_TIMEOUT = 1000;

export interface UseWebRTCConnectionOptions {
  videoId: string | null | undefined;
  onLoadingStateChange?: (loading: boolean) => void;
  onError?: (error: CategorizedVideoError | null) => void;
  onAudioAvailabilityChange?: (hasAudio: boolean) => void;
}

export interface UseWebRTCConnectionResult {
  stream: MediaStream | null;
  isConnected: boolean;
  hasAudio: boolean;
  setAudioEnabled: (enabled: boolean) => void;
}

interface OfferResponse {
  streamId?: string;
  answerSdp?: string;
}

/**
 * Hook to manage a WebRTC connection for video streaming.
 * Returns a MediaStream that can be shared across multiple video elements.
 */
export function useWebRTCConnection({
  videoId,
  onLoadingStateChange,
  onError,
  onAudioAvailabilityChange,
}: UseWebRTCConnectionOptions): UseWebRTCConnectionResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasReceivedAudioTrackRef = useRef(false);
  const cleanupRef = useRef(false);
  const combinedStreamRef = useRef<MediaStream | null>(null);

  const { videoInstance } = useVideo({ videoId: videoId ?? undefined });
  const { api } = useApi();

  // Stable callback wrappers that always call the latest callback
  const notifyLoadingStateChange = useEffectEvent((loading: boolean) => {
    onLoadingStateChange?.(loading);
  });

  const notifyError = useEffectEvent((error: CategorizedVideoError | null) => {
    onError?.(error);
  });

  const notifyAudioAvailabilityChange = useEffectEvent((hasAudioAvailable: boolean) => {
    onAudioAvailabilityChange?.(hasAudioAvailable);
  });

  // Initialize WebRTC connection
  useEffect(() => {
    if (!videoId || !videoInstance || !api) {
      if ((!videoInstance || !api) && videoId) {
        // Video or API is still loading
        return;
      }
      notifyError(categorizeVideoError('Missing video configuration'));
      return;
    }

    cleanupRef.current = false;

    async function initializeWebRTC() {
      try {
        notifyLoadingStateChange(true);
        notifyError(null);
        notifyAudioAvailabilityChange(false);
        hasReceivedAudioTrackRef.current = false;
        setIsConnected(false);
        setHasAudio(false);

        const configuration: RTCConfiguration = {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        };

        // Create a new peer connection
        const pc = new RTCPeerConnection({
          ...configuration,
          bundlePolicy: 'max-compat',
          iceCandidatePoolSize: 10,
        });
        peerConnectionRef.current = pc;

        // Add transceivers for audio and video
        pc.addTransceiver('audio', { direction: 'recvonly' });
        pc.addTransceiver('video', { direction: 'recvonly' });

        // Create a data channel if the video options require it
        if (videoInstance?.options?.dataChannel !== false) {
          pc.createDataChannel('data');
        }

        // Handle incoming tracks
        pc.ontrack = (event) => {
          if (cleanupRef.current) return;

          const track = event.track;
          if (!track) {
            console.error('useWebRTCConnection: No track in ontrack event');
            return;
          }

          // Create combined stream if it doesn't exist
          if (!combinedStreamRef.current) {
            combinedStreamRef.current = new MediaStream();
          }

          // Add the track to our combined stream
          combinedStreamRef.current.addTrack(track);

          const combinedStream = combinedStreamRef.current;
          const audioTracks = combinedStream.getAudioTracks();
          const videoTracks = combinedStream.getVideoTracks();

          // Check if stream has audio tracks
          const streamHasAudio = audioTracks.length > 0;
          if (streamHasAudio && !hasReceivedAudioTrackRef.current) {
            setHasAudio(true);
            notifyAudioAvailabilityChange(true);
            hasReceivedAudioTrackRef.current = true;
          }

          // Update stream state when we have video
          if (videoTracks.length > 0) {
            if (cleanupRef.current) return;
            setStream(combinedStream);
            setIsConnected(true);
            notifyLoadingStateChange(false);
            notifyError(null);
          }
        };

        pc.onconnectionstatechange = () => {
          if (cleanupRef.current) return;
          if (pc.connectionState === 'failed') {
            setIsConnected(false);
            notifyLoadingStateChange(false);

            const iceState = pc.iceConnectionState;
            if (iceState === 'failed') {
              notifyError(
                categorizeVideoError(
                  'WebRTC connection failed. This may be due to network incompatibility (Symmetric NAT, Carrier-Grade NAT) or firewall restrictions. If accessing remotely, ensure your router supports WebRTC connections. Try connecting from your local network or a different network (e.g., mobile data).'
                )
              );
            } else {
              notifyError(categorizeVideoError('Connection failed'));
            }
          }
        };

        // Create offer
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        });

        await pc.setLocalDescription(offer);

        // Wait for ICE candidates to be gathered
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(resolve, ICE_GATHERING_TIMEOUT);
          pc.addEventListener('icecandidate', (event) => {
            if (!event.candidate) {
              clearTimeout(timeout);
              resolve();
            }
          });
        });

        if (cleanupRef.current) return;

        // Get the final SDP after ICE gathering
        const finalOffer = pc.localDescription;
        if (!finalOffer?.sdp) {
          throw new Error('No local description available');
        }

        // Send offer to ManagerVideos
        const responseData = (await api.call({
          method: 'POST',
          path: `/api/manager/videos/video/${videoId}/offer`,
          body: {
            offer: finalOffer.sdp,
          },
          $timeout: 30000,
        })) as OfferResponse;

        if (cleanupRef.current) return;

        const { streamId, answerSdp } = responseData;

        if (!answerSdp) {
          throw new Error('No answer SDP received from server');
        }

        if (typeof answerSdp !== 'string') {
          console.error(
            'useWebRTCConnection: Invalid answerSdp type:',
            typeof answerSdp,
            answerSdp
          );
          throw new Error('Invalid answer SDP format received from server');
        }

        // Set remote description
        // Fix server SDP: when we offer recvonly, server incorrectly responds with sendrecv
        // We need to change it to sendonly to match the expected direction
        const fixedAnswerSdp = answerSdp.replace(/a=sendrecv/g, 'a=sendonly');
        const answer: RTCSessionDescriptionInit = { type: 'answer', sdp: fixedAnswerSdp };
        await pc.setRemoteDescription(answer);

        // Start keep-alive interval
        if (streamId) {
          keepAliveIntervalRef.current = setInterval(async () => {
            try {
              await api.call({
                method: 'POST',
                path: `/api/manager/videos/video/${videoId}/keep-alive`,
                body: { streamId },
                $timeout: 10000,
              });
            } catch (error) {
              console.error('useWebRTCConnection: Keep-alive error:', error);
            }
          }, 60000);
        }
      } catch (error) {
        if (cleanupRef.current) return;
        console.error('useWebRTCConnection: Error initializing WebRTC:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to establish video connection';
        setIsConnected(false);
        notifyLoadingStateChange(false);
        notifyError(categorizeVideoError(errorMessage, error instanceof Error ? error : null));
      }
    }

    initializeWebRTC();

    return () => {
      cleanupRef.current = true;
      combinedStreamRef.current = null;
      setStream(null);
      setIsConnected(false);
      setHasAudio(false);
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    };
  }, [
    videoId,
    videoInstance,
    api,
    notifyLoadingStateChange,
    notifyError,
    notifyAudioAvailabilityChange,
  ]);

  /**
   * Control audio track enabled state
   */
  const setAudioEnabled = useEffectEvent((enabled: boolean) => {
    if (peerConnectionRef.current) {
      const transceivers = peerConnectionRef.current.getTransceivers();
      const audioTransceiver = transceivers.find(
        (t) => t.receiver && t.receiver.track && t.receiver.track.kind === 'audio'
      );

      if (audioTransceiver?.receiver?.track) {
        audioTransceiver.receiver.track.enabled = enabled;
      }
    }

    // Also enable/disable audio tracks on the stream
    if (combinedStreamRef.current) {
      combinedStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  });

  return {
    stream,
    isConnected,
    hasAudio,
    setAudioEnabled,
  };
}
